import React, { useState } from 'react';
import { Layout, Menu, MenuProps, Typography } from 'antd';
import Icon, {
  BarChartOutlined,
  CheckSquareOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  SolutionOutlined,
  TeamOutlined,
  WalletOutlined
} from '@ant-design/icons';
import { Link, useLocation } from 'react-router-dom';
import ExpressLogo from '../../assets/img/expressLogo.svg?react';
import styles from './SideBar.module.less';
import { useTranslation } from 'react-i18next';
import WalletBalanceWidget from '../WalletBalanceWidget';
import { checkIsRestrictedForDisabledUsers, getRouteName, routes } from '../../utils/routerHelper';
import { useDispatch, useSelector } from 'react-redux';
import { userSelector } from '../../redux/selectors/user';
import { CreateWalletInvoiceRequest, WalletInvoiceResponse } from '../../api/models/payments';
import AddAmountModal from '../AddAmountModal/AddAmountModal';
import Button from '../Button/Button';
import { TFunction } from 'i18next';
import {
  dedicatedFleetActiveMenuClicked,
  dedicatedFleetGenerateOTPMenuClicked,
  faqMenuClicked,
  logOutClicked,
  marketplaceActiveMenuClicked,
  marketplaceCompletedMenuClicked,
  marketplaceDashboardMenuClicked,
  newDeliveryCTAMenuClicked,
  profileMenuClicked
} from '../../utils/analytika/events';
import { getAnalytikaScreenName } from '../../utils/analytika/reporterAnalytika';
import { MenuInfo } from 'rc-menu/lib/interface';
import publishAV2Event from '../../utils/analytika/reporterAnalytikaV2';
import { ANALYTIKA_EVENTS_V2 } from '../../utils/analytika/enums';
import { CustomIconComponentProps } from '@ant-design/icons/es/components/Icon';

type UnformattedMenuItem = { key: string; icon?: React.ReactNode; children?: UnformattedMenuItem[]; type?: 'group' };
type FormattedMenuItem = Required<MenuProps>['items'][number];

const siderWidth = 240;

const marletplaceItemsGroup: UnformattedMenuItem = {
  key: 'onDemand',
  children: [
    {
      key: 'activeDeliveries',
      icon: <ClockCircleOutlined />
    },
    { key: 'pastDeliveries', icon: <CheckSquareOutlined /> },
    { key: 'deliveriesDashboard', icon: <BarChartOutlined /> }
  ],
  type: 'group'
};

const dedicatedFleetItemsGroup: UnformattedMenuItem = {
  key: 'dedicatedFleet',
  children: [
    { key: 'deliveriesOtp', icon: <TeamOutlined /> },
    { key: 'activeDeliveriesOtp', icon: <ClockCircleOutlined /> }
  ],
  type: 'group'
};

const newProfileGroup: UnformattedMenuItem = {
  key: 'accountGroup',
  children: [
    { key: 'addresses', icon: <EnvironmentOutlined /> },
    { key: 'payments', icon: <WalletOutlined /> },
    { key: 'profile', icon: <SolutionOutlined /> },
    { key: 'faq', icon: <QuestionCircleOutlined /> }
  ],
  type: 'group'
};

const getNewDeliveryButton = (t: TFunction, disabled?: boolean) => ({
  key: 'deliveriesDetailed',
  disabled: true,
  label: (
    <Link to={disabled ? '#' : routes.deliveriesDetailed}>
      <Button disabled={disabled} type={'primary'} block size={'large'} icon={<PlusOutlined />}>
        {t(`sideMenu.deliveriesDetailed`)}
      </Button>
    </Link>
  )
});

const analyticsEventMapping = {
  deliveriesDetailed: newDeliveryCTAMenuClicked,
  activeDeliveries: marketplaceActiveMenuClicked,
  pastDeliveries: marketplaceCompletedMenuClicked,
  deliveriesDashboard: marketplaceDashboardMenuClicked,
  deliveriesOtp: dedicatedFleetGenerateOTPMenuClicked,
  activeDeliveriesOtp: dedicatedFleetActiveMenuClicked,
  profile: profileMenuClicked,
  faq: faqMenuClicked
};

const SideBar = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [isAmountModalVisible, setIsAmountModalVisible] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const openAmountModal = () => {
    setIsAmountModalVisible(true);
  };

  const closeAmountModal = () => {
    setIsAmountModalVisible(false);
  };

  const {
    merchantConfig: {
      isOtpEnabled,
      availablePaymentMethods: { isWalletEnabled },
      isMerchantEnabled,
      accessTypes: { isPortalAccessEnabled }
    },
    userWalletConfig: { userBalance },
    loadCompleteFor: { blockingRequests: isBlockingRequestsFinished },
    userGalileoVars: {
      portal_rollout_toggling_otp: isRolloutTogglingOtpEnabled,
      portal_is_access_type_controlled: isAccessTypeControlled
    }
  } = useSelector(userSelector);

  const {
    invoices: { createWalletInvoice },
    user: { logoutUser, publishAnalytikaEvent }
  } = useDispatch<{
    user: { logoutUser: () => Promise<void>; publishAnalytikaEvent: (value: any) => void };
    invoices: { createWalletInvoice: (request: CreateWalletInvoiceRequest) => Promise<WalletInvoiceResponse> };
  }>();

  const currentScreenName = getAnalytikaScreenName(pathname);
  const activeRouteKey = getRouteName(pathname);

  const formatMenuItem = ({ key, icon, children, type }: UnformattedMenuItem): FormattedMenuItem => {
    const isRestricted =
      type !== 'group' &&
      key !== 'logout' &&
      !isMerchantEnabled &&
      checkIsRestrictedForDisabledUsers(routes[key as keyof typeof routes] || routes.deliveriesDetailed);

    return {
      key,
      icon,
      children: children && children.map(formatMenuItem),
      disabled: isRestricted,
      label:
        type === 'group' ? (
          t(`sideMenu.${key}`).toUpperCase()
        ) : (
          <Link to={isRestricted ? '#' : routes[key as keyof typeof routes] || routes.deliveriesDetailed}>
            <Typography.Title disabled={isRestricted} style={{ marginBottom: 0 }} level={4}>
              {t(`sideMenu.${key}`)}
            </Typography.Title>
          </Link>
        ),
      type
    } as FormattedMenuItem;
  };

  const marketplaceMenuItemGroup = formatMenuItem(marletplaceItemsGroup);

  const isOrderCreationDisabled = isAccessTypeControlled && !isPortalAccessEnabled;
  //@ts-ignore
  !isOrderCreationDisabled && marketplaceMenuItemGroup.children.unshift(getNewDeliveryButton(t, !isMerchantEnabled));
  const menuItems = [marketplaceMenuItemGroup];

  const isOtpVisible = (isRolloutTogglingOtpEnabled && isOtpEnabled) || !isRolloutTogglingOtpEnabled;
  if (!isOrderCreationDisabled && isOtpVisible) {
    menuItems.push(formatMenuItem(dedicatedFleetItemsGroup));
  }

  menuItems.push(formatMenuItem(newProfileGroup));

  const logoutItem = formatMenuItem({
    key: 'logout',
    icon: <LogoutOutlined />
  });

  const menuItemHandleClick = ({ key }: MenuInfo) => {
    const getEventFunction = analyticsEventMapping[key as keyof typeof analyticsEventMapping];
    // @ts-ignore
    getEventFunction && publishAnalytikaEvent(getEventFunction(currentScreenName));
  };

  const isWalletWidgetDisplayed = isWalletEnabled && isBlockingRequestsFinished;

  return (
    <Layout.Sider
      zeroWidthTriggerStyle={{ marginTop: 15, background: 'transparent', border: 'none' }}
      collapsedWidth="0"
      breakpoint={'lg'}
      width={siderWidth}
      theme="light"
      className={styles.sider}
      trigger={isCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
      onCollapse={collapsed => setIsCollapsed(collapsed)}
    >
      <div>
        <div className={styles.logoWrapper}>
          <Link to={'/'}>
            <Icon
              component={(svgProps: CustomIconComponentProps | any) => (
                <ExpressLogo {...svgProps} width="157" height="38" />
              )}
            />
          </Link>
        </div>
        <Menu
          onClick={menuItemHandleClick}
          style={{ width: siderWidth }}
          selectedKeys={[activeRouteKey]}
          mode="inline"
          items={menuItems}
        />
      </div>
      <div>
        {isWalletWidgetDisplayed && (
          <WalletBalanceWidget
            isLoading={!isBlockingRequestsFinished}
            openAmountModal={openAmountModal}
            {...userBalance}
          />
        )}
        <Menu
          onClick={() => {
            publishAnalytikaEvent(logOutClicked(currentScreenName));
            publishAV2Event({
              event_name: ANALYTIKA_EVENTS_V2.EXPR_CLICK_LOGOUT,
              event_version: 1
            });
            logoutUser();
          }}
          activeKey={''}
          style={{ width: siderWidth }}
          mode="inline"
          items={[logoutItem]}
        />
      </div>
      {isAmountModalVisible && (
        <AddAmountModal
          currency={userBalance.currency}
          onClose={closeAmountModal}
          visible={isAmountModalVisible}
          createWalletInvoice={createWalletInvoice}
        />
      )}
    </Layout.Sider>
  );
};

export default SideBar;
